/**
 * WORN TOGETHER — three looks, one at a time.
 *
 * Two jobs. The tab row swaps which look is showing. The button then turns that look's
 * piece list into size controls, and once every piece has a size it adds the whole
 * outfit in one request. Sizes stay hidden until asked for, so the default state reads
 * as an editorial list of pieces rather than a form.
 */
if (!customElements.get('atelier-wardrobe')) {
  customElements.define(
    'atelier-wardrobe',
    class AtelierWardrobe extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-look-tab]')];
        this.panels = [...this.querySelectorAll('[data-look-panel]')];
        if (!this.panels.length) return;

        this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.show(i)));
        this.panels.forEach((panel) => this.wire(panel));
      }

      show(index) {
        this.tabs.forEach((tab, i) => {
          tab.classList.toggle('atelier-tab--active', i === index);
          tab.setAttribute('aria-selected', String(i === index));
        });
        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });
      }

      wire(panel) {
        const cta = panel.querySelector('[data-look-cta]');
        const ctaText = panel.querySelector('[data-look-cta-text]');
        const note = panel.querySelector('[data-look-note]');
        const pieces = [...panel.querySelectorAll('[data-look-piece]')];
        if (!cta || !pieces.length) return;

        const restingLabel = ctaText.textContent.trim();
        let building = false;

        const chosen = () =>
          pieces.map((piece) => {
            const size = piece.querySelector('[data-look-size][aria-pressed="true"]');
            return { id: size ? size.dataset.variant : '', price: size ? parseInt(size.dataset.price, 10) : 0 };
          });

        const refresh = () => {
          const picked = chosen();
          const complete = picked.every((p) => p.id);
          const total = picked.reduce((sum, p) => sum + p.price, 0);
          cta.disabled = !complete;
          ctaText.textContent = complete ? `ADD THE LOOK — ${AtelierWardrobe.money(total, this.dataset.currency)}` : 'SELECT EVERY SIZE';
        };

        pieces.forEach((piece) => {
          piece.querySelectorAll('[data-look-size]').forEach((chip) => {
            chip.addEventListener('click', () => {
              piece.querySelectorAll('[data-look-size]').forEach((other) => {
                other.setAttribute('aria-pressed', String(other === chip));
                
              });
              refresh();
            });
          });
        });

        cta.addEventListener('click', async () => {
          // First press reveals the sizes; the list only becomes a form once asked.
          if (!building) {
            building = true;
            pieces.forEach((piece) => {
              const sizes = piece.querySelector('[data-look-sizes]');
              if (sizes) sizes.hidden = false;
            });
            panel.classList.add('atelier-wardrobe__panel--building');
            if (note) note.textContent = 'One size per piece.';
            refresh();
            return;
          }

          const picked = chosen().filter((p) => p.id);
          if (!picked.length) return;

          cta.disabled = true;
          ctaText.textContent = 'ADDING…';
          try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: picked.map((p) => ({ id: Number(p.id), quantity: 1 })) }),
            });
            if (!response.ok) throw new Error(`Add failed (${response.status})`);
            ctaText.textContent = 'ADDED TO BAG';
            if (note) note.textContent = 'The look is in your bag.';
            if (window.dtRefreshCart) window.dtRefreshCart();
          } catch (error) {
            cta.disabled = false;
            ctaText.textContent = restingLabel;
            if (note) note.textContent = 'That did not go through. Try again.';
            console.log(`worn together: ${error.message}`);
          }
        });
      }

      static money(cents, currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency: currency || window.Shopify?.currency?.active || 'USD',
          }).format(cents / 100);
        } catch (error) {
          return `${currency || 'USD'} ${(cents / 100).toFixed(2)}`;
        }
      }
    }
  );
}

